import { Alerts } from "../../../utils/Alerts.js";
import { Notifications } from "../../../utils/Notifications.js";
import { rpc } from "../../AuthWrapper2/rpc-bridge.js";
import { AuthViewModel } from "../../AuthWrapper2/viewmodels/AuthViewModel.js";
import { ViewModel, singleton } from "mvc-kit";
//#region src/modules/Notifications/viewmodels/NotificationsViewModel.ts
var RECENT_NOTIFICATIONS_LIMIT = 10;
/**
* Maps a NotificationCreated ws payload (the shared Notification record) into
* a recipient-row shape for the recent feed. Recipient-only fields that the
* ws payload doesn't carry are stubbed — the next fetchRecent() replaces the
* row with the real thing.
*/
function toNotificationItem(notification) {
	return {
		body: notification.body,
		category: notification.category,
		cleared: false,
		history: notification.history,
		id: `live-${notification.id}`,
		link: notification.link,
		notificationId: notification.id,
		organizationId: "",
		priority: notification.priority,
		read: false,
		requiresAction: notification.requiresAction,
		sourceRef: notification.sourceRef,
		title: notification.title,
		userId: ""
	};
}
/**
* Owns THE websocket subscription for notifications, the recent feed
* (dropdown), the badge getter, and all mutation semantics. Every surface
* (bell, dropdown, toasts, profile inbox) listens to this VM — never to the
* socket directly.
*/
var NotificationsViewModel = class extends ViewModel {
	static DEFAULT_STATE = {
		errorRecent: false,
		loadingMore: false,
		loadingRecent: false,
		recentItems: [],
		total: null
	};
	/**
	* Held as an own field (not read via a bare `singleton(AuthViewModel)` call
	* inside the getter below) so mvc-kit's auto-tracking picks it up as a
	* subscribable dependency: it subscribes to the real AuthViewModel and
	* re-notifies our own listeners (so React re-renders) whenever identity
	* changes, e.g. on UserClientMetadataChanged.
	*/
	authViewModel = singleton(AuthViewModel);
	/**
	* Server-authoritative unread count (contract §6): only ever read from
	* userClientMetadata, absent = 0. Never computed or adjusted locally.
	*
	* Declared as a plain field (not a class `get`) — its real accessor is
	* installed via Object.defineProperty in the constructor, which overwrites
	* this field's descriptor. This is intentional: mvc-kit memoizes every
	* prototype getter against THIS instance's own revision (see ViewModel's
	* three-tier getter cache), and there is no dependency edge from "this
	* instance's revision" to "AuthViewModel's revision" for a value merely
	* read out of another singleton's state — a class `get` here would cache
	* the first read forever. The own-instance accessor stays always live.
	*/
	unreadCount;
	constructor(initialState) {
		super(initialState);
		Object.defineProperty(this, "unreadCount", {
			configurable: true,
			enumerable: true,
			get: () => this.authViewModel.state.identityInfo?.user?.userClientMetadata?.unread?.notifications ?? 0
		});
	}
	/**
	* Whether the server has more rows than the feed has loaded. `total` may be
	* stale between fetches (live events don't adjust it) — worst case is one
	* Load-more that appends nothing, which itself refreshes `total`.
	*/
	get hasMore() {
		const { recentItems, total } = this.state;
		return total !== null && recentItems.length < total;
	}
	onInit() {
		this.setupServerEventHandlers();
	}
	async fetchRecent() {
		const { rpcService } = rpc();
		this.set({
			errorRecent: false,
			loadingRecent: true
		});
		try {
			const { data } = await rpcService.Notifications.FindMyNotifications.v1.call({ pagination: { limit: 10 } });
			this.set({
				recentItems: data.notificationRecipients,
				total: data.total
			});
		} catch (err) {
			console.warn("Error notifications::find-my-notifications", err?.message);
			this.set({ errorRecent: true });
		} finally {
			this.set({ loadingRecent: false });
		}
	}
	/**
	* Appends the next page (skip = loaded count). Live websocket prepends
	* shift the server's offsets, so appended rows are deduped by
	* notificationId. Never throws — callers are fire-and-forget UI handlers;
	* the Load-more button itself is the retry affordance.
	*/
	async loadMore() {
		if (this.state.loadingRecent || this.state.loadingMore || !this.hasMore) return;
		const { rpcService } = rpc();
		this.set({ loadingMore: true });
		try {
			const { data } = await rpcService.Notifications.FindMyNotifications.v1.call({ pagination: {
				limit: 10,
				skip: this.state.recentItems.length
			} });
			this.set((prev) => {
				const loadedIds = new Set(prev.recentItems.map((recentItem) => recentItem.notificationId));
				return {
					recentItems: [...prev.recentItems, ...data.notificationRecipients.filter((row) => !loadedIds.has(row.notificationId))],
					total: data.total
				};
			});
		} catch (err) {
			console.warn("Error notifications::load-more", err?.message);
			Alerts.error("There was a problem loading more notifications");
		} finally {
			this.set({ loadingMore: false });
		}
	}
	/**
	* Marks one notification (id) or ALL unread (no id) read. Optimistic row
	* restyle only — the badge updates itself via UserClientMetadataChanged.
	*/
	async markRead(notificationId) {
		const { rpcService } = rpc();
		this.set((prev) => ({ recentItems: prev.recentItems.map((recentItem) => !notificationId || recentItem.notificationId === notificationId ? {
			...recentItem,
			read: true
		} : recentItem) }));
		try {
			await rpcService.Notifications.MarkNotificationsRead.v1.call(notificationId ? { notificationId } : {});
		} catch (err) {
			this.fetchRecent();
			throw err;
		}
	}
	/**
	* Race-aware resolve (contract §3.4). {resolved:false} = a teammate won:
	* SUCCESS, remove locally, toast. 404 = already gone: same treatment.
	* Any other failure alerts the user and returns null instead of throwing —
	* every caller (toast onAction, dropdown row, inbox row/detail) is
	* fire-and-forget, so a rethrow here would just leak an unhandled
	* rejection; the VM has already surfaced the error via Alerts.
	*/
	async resolve(notificationId) {
		const { rpcService } = rpc();
		try {
			const { data } = await rpcService.Notifications.ResolveNotification.v1.call({ notificationId });
			if (!data.resolved) Notifications.brief("Already handled by a teammate");
			this.removeItem(notificationId);
			this.emit("resolved", { notificationId });
			return data;
		} catch (err) {
			if (err?.code === 404) {
				this.removeItem(notificationId);
				this.emit("resolved", { notificationId });
				return null;
			}
			Alerts.error("There was a problem resolving the notification", err?.message);
			return null;
		}
	}
	removeItem(notificationId) {
		this.set((prev) => ({ recentItems: prev.recentItems.filter((recentItem) => recentItem.notificationId !== notificationId) }));
	}
	setupServerEventHandlers() {
		const { rpcClient } = rpc();
		const unsubscribe = rpcClient.subscribeToServerMessages((msg) => {
			const { data, event } = msg;
			switch (event) {
				case "NotificationCreated": {
					const { notification } = data;
					this.set((prev) => ({ recentItems: [toNotificationItem(notification), ...prev.recentItems.filter((recentItem) => recentItem.notificationId !== notification.id)] }));
					this.emit("created", notification);
					break;
				}
				case "NotificationResolved": {
					const { notificationId } = data;
					this.removeItem(notificationId);
					this.emit("resolved", { notificationId });
					break;
				}
			}
		});
		this.addCleanup(unsubscribe);
	}
};
//#endregion
export { NotificationsViewModel, RECENT_NOTIFICATIONS_LIMIT, toNotificationItem };

//# sourceMappingURL=NotificationsViewModel.js.map